import { User } from "oidc-client-ts";
import {
  getActiveUserManager,
  userManager,
  userManagerFunction,
  userManagerGoogleSSO,
  userManagerOBO
} from "../userManagers";
import { RootState } from "./types";
import { defineStore } from "pinia";
import membersApi, { Member } from "@/api/members.api";
import * as Sentry from "@sentry/vue";
import ldClient from "@/utils/launchDarkly";
import {
  identifyLaunchDarklyApplication,
  identifyLaunchDarklyUser
} from "@/utils/launchDarkly/ldHelper";

export const useRootStore = defineStore("root", {
  state: (): RootState => ({
    authenticated: false,
    member: undefined,
    invalid: false
  }),
  persist: {
    storage: localStorage,
    pick: ["authenticated", "member"]
  },
  share: {
    enable: true,
    initialize: true,
    omit: ["continueTo"]
  },
  actions: {
    async login({
      email,
      password
    }: {
      email: string;
      password: string;
    }): Promise<string | undefined> {
      const result = await userManager.signinResourceOwnerCredentials({
        username: email,
        password: password
      });

      sessionStorage.setItem("authType", "R");

      if (result) {
        await this.loadMemberData();
      } else {
        await this.logout();
        return undefined;
      }
      let roles: string[] = [];
      if (typeof result?.profile?.role === "string") {
        roles = [result?.profile?.role];
      } else if (Array.isArray(result?.profile?.role)) {
        roles = result?.profile?.role;
      }
      Sentry.setUser({
        id: result?.profile?.sub,
        email: result?.profile?.email,
        ip_address: "{{auto}}",
        roles: roles.join(",")
      });
      this.authenticated = true;
      await identifyLaunchDarklyUser(
        (context) => ldClient.identify(context),
        result.profile
      );
      return result.access_token;
    },
    async loginGoogle(): Promise<User | undefined> {
      const result = await userManagerGoogleSSO.signinRedirectCallback();
      sessionStorage.setItem("authType", "G");
      if (result) {
        if (!result.profile?.IsPartial) {
          await this.loadMemberData();
        }
      } else {
        await this.logout();
        return undefined;
      }

      let roles: string[] = [];
      if (typeof result?.profile?.role === "string") {
        roles = [result?.profile?.role];
      } else if (Array.isArray(result?.profile?.role)) {
        roles = result?.profile?.role;
      }
      Sentry.setUser({
        id: result?.profile?.sub,
        email: result?.profile?.email,
        ip_address: "{{auto}}",
        roles: roles.join(",")
      });
      this.authenticated = true;
      await identifyLaunchDarklyUser(
        (context) => ldClient.identify(context),
        result.profile
      );
      return result;
    },
    async loginFunction(): Promise<User | undefined> {
      // Uses signinRedirectCallback like Google SSO (standard OIDC flow)
      const result = await userManagerFunction.signinRedirectCallback();
      sessionStorage.setItem("authType", "F");
      if (result) {
        if (!result.profile?.IsPartial) {
          await this.loadMemberData();
        }
      } else {
        await this.logout();
        return undefined;
      }

      let roles: string[] = [];
      if (typeof result?.profile?.role === "string") {
        roles = [result?.profile?.role];
      } else if (Array.isArray(result?.profile?.role)) {
        roles = result?.profile?.role;
      }
      Sentry.setUser({
        id: result?.profile?.sub,
        email: result?.profile?.email,
        ip_address: "{{auto}}",
        roles: roles.join(",")
      });
      this.authenticated = true;
      await identifyLaunchDarklyUser(
        (context) => ldClient.identify(context),
        result.profile
      );
      return result;
    },
    async loginOBO(): Promise<void> {
      await userManagerOBO.signinRedirect();
    },
    async loginOBOCallback(): Promise<User | undefined> {
      const result = await userManagerOBO.signinRedirectCallback();
      sessionStorage.setItem("authType", "O");

      if (result) {
        if (!result.profile?.IsPartial) {
          await this.loadMemberData();
        }
      } else {
        await this.logout();
        return undefined;
      }
      let roles: string[] = [];
      if (typeof result?.profile?.role === "string") {
        roles = [result?.profile?.role];
      } else if (Array.isArray(result?.profile?.role)) {
        roles = result?.profile?.role;
      }
      Sentry.setUser({
        id: result?.profile?.sub,
        email: result?.profile?.email,
        ip_address: "{{auto}}",
        roles: roles.join(",")
      });
      this.authenticated = true;
      await identifyLaunchDarklyUser(
        (context) => ldClient.identify(context),
        result.profile
      );
      return result;
    },
    async logout() {
      // Check if we're not authenticated already and don't do the logout if we aren't as otherwise we get into a loop of logout 401ing
      if (!this.authenticated) {
        return;
      }

      try {
        const aum = await getActiveUserManager();
        // This replaces signoutSilent, as its impossible to make it not send id_token_hint in query string and still have it work.
        // Do not await: logout must not block on IdP hang/failure; only handle rejection to avoid Sentry noise.
        fetch(
          `${import.meta.env.VITE_APP_INDIVIDUALS_URL}/member/connect/logout`,
          {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${(await aum?.getUser())?.access_token || ""}`,
              "Content-Type": "application/x-www-form-urlencoded"
            },
            body: `id_token_hint=${(await aum?.getUser())?.id_token ?? ""}`
          }
        ).catch((e: unknown) => {
          console.warn(e);
        });
        aum?.removeUser();
        sessionStorage.removeItem("authType");
      } catch (e) {
        console.error(e);
        // above call doesn't seem to always work, so force clear it as well
        Object.keys(localStorage).forEach(key => {
          if (key?.startsWith("oidc.user")) {
            localStorage.removeItem(key);
          }
        });
      } finally {
        sessionStorage.clear();
        this.authenticated = false;
        this.member = undefined;
        identifyLaunchDarklyApplication((context) => ldClient.identify(context));
        dispatchEvent(new Event("loggedOut"));
      }
    },
    setMember(member: Member) {
      this.member = member;
    },
    setAuthenticated(authenticated: boolean) {
      this.authenticated = authenticated;
    },
    setContinueToPath(path: string) {
      this.continueTo = path;
    },
    async loadMemberData() {
      const member = await membersApi.loadMemberData();
      if (member) {
        this.member = member;
      }
      return member;
    },
    async updateMember(keyValues: Record<string, any>) {
      const member = await membersApi.updateMember(keyValues);
      if (member) {
        this.member = member;
        return true;
      }
      return false;
    }
  }
});
